import React from 'react';
import './Modal.scss';
import classNames from 'classnames';

import {
  Modal as ReactModal,
  Button as ReactButton,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import MenuBarExpandContext from 'contexts/menuBarExpandContext';
import { ElementScrollControl } from 'components/ElementScrollControl/ElementScrollControl';

interface ButtonFunction {
  (e?: any): void;
}

interface Button {
  title: string;
  disabled?: boolean;
  variant?: any;
  callback: ButtonFunction;
  /**
   * 被停用的原因，hover（或鍵盤 focus 到附近）才浮出來。只有 disabled 時會顯示——
   * 按鈕能按就沒有原因要講。
   */
  tooltip?: string;
}

export type ModalProps = {
  readonly title?: string;
  readonly renderTitle?: () => React.JSX.Element;
  readonly primaryButton?: Button;
  readonly secondaryButton?: Button;
  readonly dangerButton?: Button;
  readonly dismiss?: ButtonFunction;
  readonly enableCloseBtn: boolean;
  readonly className?: string;
  readonly animation?: boolean;
  readonly fullScreen?: boolean;
  readonly enforceFocus: boolean;
  readonly backdrop?: boolean | 'static';
  readonly reverseFooter?: boolean;
  readonly children?: React.ReactNode;
  readonly centerButtons?: boolean;
};

export class Modal extends React.Component<ModalProps> {
  static defaultProps = {
    enableCloseBtn: false,
    enforceFocus: true,
    backdrop: true
  };

  hide = () => {
    this.props.dismiss && this.props.dismiss();
  };

  close = () => {
    this.props.dismiss && this.props.dismiss();
  };

  /**
   * 停用中又有原因可講時，用 OverlayTrigger 掛上提示。
   *
   * 提示走 OverlayTrigger 而不是自己畫一個絕對定位的框：footer 只有 74px 高，自畫的框
   * 往上浮就會跟 modal 內容疊在一起，長一點的句子還會往旁邊衝出可視範圍。OverlayTrigger
   * 是 render 到 portal，不受 modal 內部的裁切和堆疊影響，也會自己避開畫面邊界。
   *
   * 外面要再包一層 span：disabled 的按鈕不吃滑鼠事件，trigger 直接掛在按鈕上收不到
   * hover，提示永遠出不來。span 沒有 disabled，配上按鈕 pointer-events: none 不吞事件，
   * 滑到按鈕上也算 hover 到 span。
   *
   * span 要給 tabIndex：disabled 的按鈕不在 tab 順序裡，span 預設也不能 focus，
   * 少了它 trigger 的 'focus' 永遠不會發生，只用鍵盤的人就看不到被擋住的原因。
   * role='button' + aria-disabled 是讓輔助科技把這層讀成「一顆停用中的按鈕」，
   * aria-describedby 則把原因綁在它身上——tooltip 的 id 要跟 Tooltip 一致。
   */
  renderWithTooltip = (button: Button, element: React.JSX.Element) => {
    if (!button.tooltip || !button.disabled) {
      return element;
    }
    const tooltipId = `modalBtnTooltip-${button.title}`;
    return (
      <OverlayTrigger
        placement='top'
        trigger={['hover', 'focus']}
        overlay={<Tooltip id={tooltipId}>{button.tooltip}</Tooltip>}
      >
        <span
          className='btnWithTooltip'
          tabIndex={0}
          role='button'
          aria-disabled={true}
          aria-describedby={tooltipId}
        >
          {element}
        </span>
      </OverlayTrigger>
    );
  };

  render() {
    const dangerButton = this.props.dangerButton;
    const secondaryButton = this.props.secondaryButton;
    const primaryButton = this.props.primaryButton;
    const footerElements: (React.JSX.Element | undefined)[] = [
      <>
        {primaryButton &&
          this.renderWithTooltip(
            primaryButton,
            <ReactButton
              size='sm'
              variant={
                primaryButton.variant ? primaryButton.variant : 'primary'
              }
              onClick={primaryButton.callback}
              disabled={
                primaryButton.disabled !== undefined
                  ? primaryButton.disabled
                  : false
              }
            >
              {primaryButton.title}
            </ReactButton>
          )}
        {secondaryButton && (
          <ReactButton
            size='sm'
            variant={
              secondaryButton.variant ? secondaryButton.variant : 'secondary'
            }
            onClick={secondaryButton.callback}
            disabled={
              secondaryButton.disabled !== undefined
                ? secondaryButton.disabled
                : false
            }
          >
            {secondaryButton.title}
          </ReactButton>
        )}
      </>,
      dangerButton && (
        <ReactButton
          size='sm'
          variant={dangerButton.variant ? dangerButton.variant : 'secondary'}
          onClick={dangerButton.callback}
          disabled={
            dangerButton.disabled !== undefined ? dangerButton.disabled : false
          }
        >
          {dangerButton.title}
        </ReactButton>
      )
    ];
    return (
      <MenuBarExpandContext.Consumer>
        {(expand) => {
          const className = classNames(
            this.props.className ? this.props.className : 'modal',
            {
              fullScreen: !!this.props.fullScreen,
              menuBarExpand: expand,
              centerButtons: !!this.props.centerButtons,
              customTitle: !!this.props.renderTitle
            }
          );
          !!this.props.reverseFooter && footerElements.reverse();
          return (
            <ReactModal
              className={className}
              centered
              show
              onHide={this.hide}
              enforceFocus={this.props.enforceFocus}
              animation={
                this.props.animation !== undefined ? this.props.animation : true
              }
              backdrop={this.props.backdrop}
            >
              <ReactModal.Header closeButton={this.props.enableCloseBtn}>
                {this.props.title ? (
                  <h4>{this.props.title}</h4>
                ) : this.props.renderTitle ? (
                  this.props.renderTitle()
                ) : null}
              </ReactModal.Header>
              <ReactModal.Body>
                {this.props.fullScreen ? (
                  <ElementScrollControl>
                    {this.props.children}
                  </ElementScrollControl>
                ) : (
                  this.props.children
                )}
              </ReactModal.Body>
              <ReactModal.Footer>
                {footerElements.map((element, index) => (
                  <div key={index}>{element}</div>
                ))}
              </ReactModal.Footer>
            </ReactModal>
          );
        }}
      </MenuBarExpandContext.Consumer>
    );
  }
}
